import { LIMIT } from "@/constants";
import { Order } from "@/interfaces";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pick } from "lodash";
import { useState } from "react";
import "./orderList.css";
import { dateFormateDDMMYYYY, getStatusColors } from "@/utils/common";
import { currencyFormatter } from "@/utils";
import { OrderStateEnum } from "@/enum";
import { Chip } from "@/components/views";
const filterIcon = "/icons/filter.png";
import { InfiniteScrollList } from "@/components/common/scroll";
import { useGetOrderListQuery } from "@/features/order";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

export const Route = createFileRoute("/(app)/(store)/order/")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const limit = LIMIT * 3;
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("All Orders");
  const { data: orders = [] } = useGetOrderListQuery({
    limit,
    page,
    ["seller-store-detail"]: true,
    ["order-detail"]: true,
    ["buyer-detail"]: true,
    ...(filter !== "All Orders" ? { status: filter } : {}),
  });

  const renderOrderItem = (item: Order) => {
    const { statusColor, bgColor } = getStatusColors(
      item.status !== OrderStateEnum.Cancelled ? item?.payment?.paymentStatus as "default" | "Completed" | "Cancelled" | "Processing" | "Shipped" | "Refunded" | "Paid" | "Due" | "Premium" | "Active" | "Inactive" | "PartialPaid" | undefined : item.status
    );

    const visibleProducts = item.itemList?.slice(0, 2) || [];
    const remainingProductsCount = Math.max(
      (item.itemList?.length || 0) - 2,
      0
    );

    return (
      <div
        className="orderItem"
        key={item._id}
        onClick={() =>
          navigate({
            to: "/order/$orderId",
            params: { orderId: item._id },
          })
        }
      >
        <div className="orderItemGradient">
          <div className="orderItemHeader">
            <div className="orderInfo">
              <p className="billTotal">
                Bill Total:{" "}
                <span className="billAmount">
                  {currencyFormatter(item.totalAmount)}
                </span>
              </p>
              <text className="caption">{item?.sellerStore?.name}</text>
              <text className="tag">#{item?.invoiceNumber}</text>
            </div>

            <div className="statusBadge" style={{ backgroundColor: bgColor }}>
              <span style={{ color: statusColor }}>
                {item.status !== "Cancelled" ? item.payment?.paymentStatus : item.status}
              </span>
            </div>
          </div>
        </div>

        <div className="orderItemContent">
          {visibleProducts.length > 0 ? (
            <>
              <div className="productsColumn">
                {visibleProducts.map((productItem, index) => (
                  <div key={index} className="productRow">
                    {productItem.product?.image && (
                      <img
                        className="productImage"
                        src={productItem.product.image}
                      />
                    )}
                    <span className="quantityText body">
                      {productItem.unitWiseQuantity?.quantity}
                    </span>
                    <span className="productName body">
                      {" X "+ productItem.product?.name || "Product not specified"}
                    </span>
                  </div>
                ))}
              </div>

              {remainingProductsCount > 0 && (
                <div className="remainingProducts">
                  <text className="caption">
                    +{remainingProductsCount} more item
                    {remainingProductsCount !== 1 ? "s" : ""}
                  </text>
                </div>
              )}
            </>
          ) : (
            <div className="noProductsContainer">
              <text className="noProductsText">No products in this order</text>
            </div>
          )}

          <div className="statusRow">
            <text className="caption">
              {item.payment?.paymentStatus === "Paid" ? "Paid on" : "Due on"}{" "}
              {dateFormateDDMMYYYY(item.createdAt)}
            </text>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="orderListContainer">
      {/* <Header title="Orders" /> */}
      <div className="filterHeader">
        <div className="filterContainer">
          <div className="filterScroll">
            <div className="filter">
              <img src={filterIcon} alt="filter" />
              <span>Filter</span>
            </div>
            <Chip
              className={
                filter === "All Orders" ? "activeFilter" : "filterChip"
              }
              onClick={() => (setFilter("All Orders"), setPage(1))}
            >
              All Orders
            </Chip>
            {Object.values(
              pick(OrderStateEnum, ["Cancelled", "Delivered"])
            ).map((item) => (
              <Chip
                className={filter === item ? "activeFilter" : "filterChip"}
                key={item}
                onClick={() => (setFilter(item), setPage(1))}
              >
                {item}
              </Chip>
            ))}
          </div>

          <div className="createOrderContainer">
            <Button onClick={() => navigate({ to: "/invoice/create-invoice" })}>
              <Plus className="w-4 h-4 mr-2" />
              Create New Order
            </Button>
          </div>
        </div>
      </div>
      <div className="orderList">
        <InfiniteScrollList
          items={orders}
          renderItem={renderOrderItem}
          loadMore={() => setPage((pre) => pre + 1)}
          hasMore={limit * page <= orders?.length}
          maxHeight={"90dvh"}
        />
      </div>
    </div>
  );
}
